import * as React from "react";
// Components
import { useTheme } from "@fluentui/react";
import { Dropdown as FluentDropdown, mergeClasses, Option, Tooltip } from "@fluentui/react-components";
// Constants
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
// Styles
import { useStyles } from "./Dropdown.styles";
import { useDebouncedCallback } from "../hooks/useDebouncedCallback";
import { customizeStyles } from "../utilities/StyleUtilities";
/**
 * Presentational Dropdown component using Fluent UI v9.
 */ export const Dropdown = ({ options, onOptionChange, selectedKey, label, debounceInterval, customStyles, placeholder, onRenderOption, onRenderTitle, disabled, dropdownRef, title, ariaLabel, disableTooltip, ...props })=>{
    const classNames = customizeStyles(useStyles(), customStyles);
    const selectedOption = options.find((o)=>o.key === selectedKey);
    const isHighContrast = useTheme().name === PlannerTheme.HighContrast;
    const optionElements = options.map((option)=>/*#__PURE__*/ React.createElement(Option, {
            key: String(option.key),
            value: String(option.key),
            text: option.text,
            disabled: option.disabled,
            "data-index": option.index,
            className: mergeClasses(classNames.dropdownItem, selectedKey === option.key && classNames.selectedDropdownItem, isHighContrast && classNames.highContrastDropdownIcon)
        }, onRenderOption ? onRenderOption(option) : /*#__PURE__*/ React.createElement("div", {
            className: classNames.optionButton
        }, option?.icon && /*#__PURE__*/ React.createElement("span", {
            className: classNames.optionIcon
        }, option.icon), /*#__PURE__*/ React.createElement("span", {
            className: classNames.optionText
        }, option.text))));
    const debouncedOnChange = useDebouncedCallback(onOptionChange, debounceInterval ?? 0);
    const handleOptionSelect = (optionValue)=>{
        //Fluent Dropdown optionValue is always string, so we need to map it back to the original type T
        const selectedOption = options.find((option)=>String(option.key) === optionValue);
        if (selectedOption) {
            debounceInterval ? debouncedOnChange(selectedOption.key) : onOptionChange?.(selectedOption.key);
        }
    };
    const tooltipContent = (title || selectedOption?.text || placeholder || "").trim();
    const dropdownElement = /*#__PURE__*/ React.createElement(FluentDropdown, {
        ...props,
        // selectedOptions can be undefined as per props but it is crashing in that case, hence passing empty array
        selectedOptions: selectedKey !== undefined ? [
            String(selectedKey)
        ] : [],
        disabled: disabled,
        value: String(selectedKey),
        className: classNames.dropdown,
        "aria-label": ariaLabel || label,
        title: disableTooltip ? title : undefined,
        onOptionSelect: (_, data)=>{
            if (data.optionValue !== undefined) {
                handleOptionSelect(data.optionValue);
            }
        },
        listbox: {
            className: classNames.listbox
        },
        ref: dropdownRef,
        onKeyDown: (e)=>{
            e.stopPropagation();
        },
        button: {
            className: classNames.button,
            children: onRenderTitle ? onRenderTitle(selectedOption) : /*#__PURE__*/ React.createElement("div", {
                className: classNames.dropdownButton
            }, selectedOption?.icon && /*#__PURE__*/ React.createElement("span", {
                className: classNames.optionIcon
            }, selectedOption.icon), /*#__PURE__*/ React.createElement("span", {
                className: classNames.optionText
            }, selectedOption?.text || placeholder))
        }
    }, optionElements);
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, label && /*#__PURE__*/ React.createElement("label", {
        className: classNames.label
    }, label), disableTooltip ? dropdownElement : tooltipContent ? /*#__PURE__*/ React.createElement(Tooltip, {
        content: tooltipContent,
        relationship: "label",
        positioning: "above",
        withArrow: true
    }, dropdownElement) : dropdownElement);
};
